import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pin, PinOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { viewDataQuery, viewDetailQuery } from '../lib/queries';
import type { ViewSummary } from '../lib/schemas';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import { ViewRenderer } from './view-renderer';

interface ViewCardProps {
  view: ViewSummary;
  role: string;
  onMutated?: () => void;
}

export function ViewCard({ view, role, onMutated }: ViewCardProps) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: viewDetail } = useQuery(viewDetailQuery(view.view_id));
  const { data: viewData, isLoading } = useQuery(viewDataQuery(view.view_id));

  const pinMutation = useMutation({
    mutationFn: (pinned: boolean) =>
      api.patch<{ status: string }>(`/api/views/${view.view_id}`, { pinned }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['views'] });
      onMutated?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<{ status: string }>(`/api/views/${view.view_id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['views'] });
      onMutated?.();
    },
  });

  const isFarmer = role === 'farmer';
  const rows = viewData?.rows ?? [];
  const executedAt = viewData?.executed_at;

  return (
    <>
      <Card className="bg-ndd-950 border-ndd-800">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base">{view.title}</CardTitle>
              <Badge variant="outline" className="text-xs text-ndd-400 border-ndd-700">
                {view.type.replace(/_/g, ' ')}
              </Badge>
            </div>
            {isFarmer && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-ndd-400 hover:text-ndd-200"
                  onClick={() => pinMutation.mutate(!view.pinned)}
                  disabled={pinMutation.isPending}
                  title={view.pinned ? 'Unpin' : 'Pin to home'}
                >
                  {view.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-red-400 hover:text-red-200"
                  onClick={() => setConfirmDelete(true)}
                  disabled={deleteMutation.isPending}
                  title="Delete view"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            )}
          </div>
          {executedAt && (
            <p className="text-xs text-ndd-600 mt-1">
              Last updated: {new Date(executedAt).toLocaleString()}
            </p>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <Skeleton className="w-full h-48" />
          ) : (
            <ViewRenderer
              type={view.type}
              chartConfig={viewDetail?.chart_config ?? {}}
              data={rows}
              isLoading={false}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-ndd-950 border-ndd-800">
          <DialogHeader>
            <DialogTitle>Delete View</DialogTitle>
            <DialogDescription className="text-ndd-400">
              Are you sure you want to delete &quot;{view.title}&quot;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              className="text-ndd-300"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteMutation.mutate();
                setConfirmDelete(false);
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
